import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TemplateProps } from '../types';
import PincoSbpPage from './pages/PincoSbpPage';
import PincoUploadPage from './pages/PincoUploadPage';
import PincoSuccessPage from './pages/PincoSuccessPage';
import PincoSlotPage from './pages/PincoSlotPage';
import PincoRetryPage from './pages/PincoRetryPage';
import {
  PincoProcessingScreen,
  PincoReceiptUnconfirmedScreen,
  PincoPaymentExpiredScreen,
  PincoErrorScreen,
  PincoExpiredScreen,
  PincoServerErrorScreen,
} from './pages/PincoProcessingPage';
import Spinner from '../../components/Spinner';
import PincoVariantPage from './pages/PincoVariantPage';
import { supportTarget } from '../../services/supportUrl';

// Native card/SBP methods that render on our page (PincoSbpPage). Redirect methods are handled by
// TransactionPage (redirect_url + paymentUrl), so they are intentionally NOT listed here (CPAYMENT-1661).
const SUPPORTED_METHODS = [
  'SBP P2P RUB',
  'SBP MATCHING RUB',
  'TRANS SBP P2P RUB',
  'OZON SBP RUB',
  'ALFA SBP RUB',
  'SBER SBP RUB',
  'VTB SBP RUB',
  'TBANK SBP RUB',
  'MC/VISA/MIR P2P RUB',
  'P2P MATCHING RUB',
  'MC/VISA P2P KZT',
  'MOBILE P2P KZT',
  'MC/VISA P2P KRW',
  'MC/VISA UAH',
  'PRIVAT24 P2P UAH',
  'M10AZN',
];

const PincoTemplate = ({
  transaction,
  loading,
  error,
  timeLeft,
  timeoutScreen,
  uuid,
  showConfirm,
  transgranError,
  receiptIntent,
  lowTimeWarning,
  onExtendTime,
  onDismissLowTime,
  onConfirm,
  onCancel,
  onDeclineOffer,
  cancelReasons,
  onLeave,
  onRedirect,
  onSelectVariant,
  variantSubmitting,
  setRetry,
  setShowConfirm,
  setTransgranError,
  setLoading,
  formatTime,
}: TemplateProps) => {
  const { t } = useTranslation();
  const [receiptAccepted, setReceiptAccepted] = useState(false);
  const [processingAfterUpload, setProcessingAfterUpload] = useState(false);
  const [serverError, setServerError] = useState(false);
  // CPAYMENT-1616: bridge the ~3s until the poll clears a stale "failed" after a re-upload.
  const [reuploading, setReuploading] = useState(false);

  const formattedTime = formatTime(timeLeft ?? 600);

  const handleConfirmWithReceipt = async () => {
    try {
      await onConfirm();
      setReceiptAccepted(true);
      setProcessingAfterUpload(true);
    } catch {
      setServerError(true);
    }
  };

  // CPAYMENT-1616: re-upload in FILES_AUTO_VERIFICATION replaces the file server-side (no re-confirm).
  const handleReupload = async () => {
    setReuploading(true);
  };

  useEffect(() => {
    if (transaction?.receiptVerification?.phase === 'pending') {
      setReuploading(false);
    }
  }, [transaction?.receiptVerification?.phase]);

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return <Spinner />;
  }

  // ─── Ожидание кончилось ничем (CPAYMENT-1716) ───────────────────────────────
  // Ветка стоит выше showConfirm: раньше клиент с истёкшим тикетом оставался на форме загрузки
  // чека, потому что EXPIRED ловился только внутри processingAfterUpload.
  if (timeoutScreen && transaction) {
    return timeoutScreen === 'payment-expired' ? (
      <PincoPaymentExpiredScreen
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onContactSupport={() => onRedirect(supportTarget(transaction))}
      />
    ) : (
      <PincoReceiptUnconfirmedScreen
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onContactSupport={() => onRedirect(supportTarget(transaction))}
      />
    );
  }

  // ─── Error / no transaction ─────────────────────────────────────────────────
  if (error || !transaction) {
    return (
      <PincoServerErrorScreen
        transaction={transaction!}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onReturn={() => transaction && onRedirect(transaction.redirectUrl)}
      />
    );
  }

  // ─── CLIENT_PENDING — adapter not found ─────────────────────────────────────
  if (
    transaction.status === 'CLIENT_PENDING' &&
    !transaction.requisite?.type
  ) {
    if ((transaction as any).paymentAdapterSearchAttemptsCounter > 0) {
      // Retryable — show retry page
      return (
        <PincoRetryPage
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onRetry={() => setRetry(prev => prev + 1)}
          onBack={() => onRedirect(transaction.redirectUrl)}
        />
      );
    }
    // No attempts left — show error
    return (
      <PincoErrorScreen
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onFinish={() => onRedirect(transaction.redirectUrl)}
      />
    );
  }

  // ─── Deposit variant selection → amount grid (CPAYMENT-1542) ────────────────
  if (transaction.status === 'WAITING_FOR_VARIANT_SELECTION') {
    return (
      <PincoVariantPage
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formatTime(timeLeft || 0)}
        onDeclineOffer={onDeclineOffer}
        onSelectVariant={onSelectVariant}
        variantSubmitting={variantSubmitting}
      />
    );
  }

  // ─── Waiting for requisites → slot machine ──────────────────────────────────
  if (transaction.status === 'WAITING_FOR_REQUISITE') {
    return (
      <PincoSlotPage
        onBack={() => onRedirect(transaction.redirectUrl)}
        uuid={uuid || ''}
        timeLeft={formattedTime}
      />
    );
  }

  // ─── Success ────────────────────────────────────────────────────────────────
  if (transaction.status === 'COMPLETED') {
    return (
      <PincoSuccessPage
        transaction={transaction}
        uuid={uuid || ''}
        onRedirect={() => onRedirect(transaction.redirectUrl)}
      />
    );
  }

  // ─── Files verification (final for client) ─────────────────────────────────
  // Для клиента это конец пути: чек у оператора, дальше ждать нечего. Показываем привычный
  // экран с галочкой и возвратом к мерчанту (как было до CPAYMENT-1616), а не спиннер.
  if (transaction.status === 'FILES_VERIFICATION') {
    // Чек так и не приняли (исчерпаны попытки / не дождались ответа проверки) — галочка тут
    // соврала бы. Клиенту нужен понятный следующий шаг: обращение в поддержку мерчанта.
    if (transaction.receiptVerification?.phase === 'unconfirmed') {
      return (
        <PincoReceiptUnconfirmedScreen
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onContactSupport={() => onRedirect(supportTarget(transaction))}
        />
      );
    }
    return (
      <PincoSuccessPage
        transaction={transaction}
        uuid={uuid || ''}
        onRedirect={() => onRedirect(transaction.redirectUrl)}
      />
    );
  }

  // ─── Receipt auto-verification (CPAYMENT-1616) ──────────────────────────────
  if (transaction.status === 'FILES_AUTO_VERIFICATION') {
    const rv = transaction.receiptVerification;
    if (rv?.phase === 'failed' && rv.canReupload && !reuploading) {
      return (
        <PincoUploadPage
          transaction={transaction}
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onConfirm={handleReupload}
          onBack={() => onRedirect(transaction.redirectUrl)}
          reupload
        />
      );
    }
    return (
      <PincoProcessingScreen
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onBack={() => onRedirect(transaction.redirectUrl)}
        receiptAccepted={false}
      />
    );
  }

  // ─── Upload page (showConfirm) ───────────────────────────────────────────────
  if (showConfirm || processingAfterUpload) {
    if (processingAfterUpload && !serverError) {
      // Step 3 — processing after receipt uploaded
      if (transaction.status === 'COMPLETED') {
        return (
          <PincoSuccessPage
            transaction={transaction}
            uuid={uuid || ''}
            onRedirect={() => onRedirect(transaction.redirectUrl)}
          />
        );
      }
      if (transaction.status === 'FILES_VERIFICATION') {
        if (transaction.receiptVerification?.phase === 'unconfirmed') {
          return (
            <PincoReceiptUnconfirmedScreen
              uuid={uuid || ''}
              timeLeft={formattedTime}
              onContactSupport={() => onRedirect(supportTarget(transaction))}
            />
          );
        }
        return (
          <PincoSuccessPage
            transaction={transaction}
            uuid={uuid || ''}
            onRedirect={() => onRedirect(transaction.redirectUrl)}
          />
        );
      }
      if (transaction.status === 'DECLINED' || transaction.status === 'EXPIRED') {
        return (
          <PincoErrorScreen
            transaction={transaction}
            uuid={uuid || ''}
            timeLeft={formattedTime}
            onFinish={() => onRedirect(transaction.redirectUrl)}
            receiptAccepted={receiptAccepted}
          />
        );
      }
      return (
        <PincoProcessingScreen
          transaction={transaction}
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onBack={() => {
            setProcessingAfterUpload(false);
            setShowConfirm(true);
          }}
        />
      );
    }

    if (serverError) {
      return (
        <PincoServerErrorScreen
          transaction={transaction}
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onReturn={() => onRedirect(transaction.redirectUrl)}
        />
      );
    }

    return (
      <PincoUploadPage
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onConfirm={handleConfirmWithReceipt}
        onBack={() => onRedirect(transaction.redirectUrl)}
      />
    );
  }

  // ─── Declined ───────────────────────────────────────────────────────────────
  if (transaction.status === 'DECLINED') {
    return (
      <PincoErrorScreen
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onFinish={() => onRedirect(transaction.redirectUrl)}
        receiptAccepted={receiptAccepted}
      />
    );
  }

  // ─── Expired ────────────────────────────────────────────────────────────────
  if (transaction.status === 'EXPIRED') {
    return (
      <PincoExpiredScreen
        transaction={transaction}
        uuid={uuid || ''}
        onFinish={() => onRedirect(transaction.redirectUrl)}
      />
    );
  }

  // ─── Main payment screen (SBP + P2P card) ──────────────────────────────────
  const isSupported = SUPPORTED_METHODS.includes(transaction.paymentMethod);

  if (
    (transaction.status === 'WAITING_FOR_SMS' || transaction.status === 'PAYMENT_PENDING') &&
    isSupported
  ) {
    return (
      <PincoSbpPage
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onCancel={onCancel}
        onDeclineOffer={onDeclineOffer}
        cancelReasons={cancelReasons}
        onLeave={onLeave}
        receiptIntent={receiptIntent}
        lowTimeWarning={lowTimeWarning}
        onExtendTime={onExtendTime}
        onDismissLowTime={onDismissLowTime}
        onConfirm={() => onConfirm()}
        onShowUpload={() => setShowConfirm(true)}
      />
    );
  }

  // Fallback spinner for unhandled states
  return <Spinner />;
};

export default PincoTemplate;
